<template>
  <WdsDrawer
    v-model:show="show"
    position="right"
    width="365px"
    title="Filters"
    closable
    dismissible
    class="!tw-bg-true-gray-50 tw-overflow-auto"
  >
    <WdsStack direction-column justify-space-between class="tw-flex-1 tw-pt-6">
      <WdsStack direction-column gap-md>
        <GraphqlDataTableFilterGroup
          v-for="(filters, group) in filterGroups"
          :key="group"
          v-model="activeFilterValues"
          :filters="filters"
          :group="group"
        />
      </WdsStack>
      <WdsStack justify-end class="tw-w-full">
        <WdsButton
          class="!tw-mt-4 !tw-mb-2"
          :disabled="!displayResetFiltersButton"
          data-test="tc-filter-reset"
          primary
          @click="resetAllFilters"
        >
          <div class="tw-my-auto">
            {{ $t('Clear all') }}
          </div>
        </WdsButton>
      </WdsStack>
    </WdsStack>
  </WdsDrawer>
</template>

<script lang="ts" setup>
import { WdsButton, WdsDrawer, WdsStack } from '@worksome/wds-components'
import { computed, watch, watchEffect } from 'vue'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useQueryArgs } from '@/components/GraphqlDataTable/composables'
import useQueryEncoder from '@/composables/useQueryEncoder'
import Filters from '@/components/GraphqlDataTable/filters'
import { FilterGroups } from '@/components/GraphqlDataTable/filters/filter-enums'
import GraphqlDataTableFilterGroup from '@/components/GraphqlDataTable/filters/GraphqlDataTableFilterGroup.vue'
import {
  countActiveFilters,
  resetFilters,
} from '@/components/GraphqlDataTable/filters/filter-tools'
import { filterObject } from '@/utils/tools'
import { FilterMetaDataProp } from '@/components/GraphqlDataTable/filters/types'

const emit = defineEmits<{
  numberOfFilters: [num: number]
}>()

interface TableFilterProps {
  query: string
  displayAllArgs?: boolean
  filterBlackList?: string[]
}

const show = defineModel<boolean>('show')

const props = withDefaults(defineProps<TableFilterProps>(), {
  displayAllArgs: false,
  filterBlackList: () => [],
})

const reactiveQuery = computed(() => props.query)
const activeFilterValues = defineModel<object>('filters', { default: () => ({}) })

const displayResetFiltersButton = computed(
  () => countActiveFilters(Object.values(activeFilterValues.value)) > 0,
)

const { args } = useQueryArgs(reactiveQuery, false, props.filterBlackList)

watchEffect(() => emit('numberOfFilters', args.value.length))

const filterGroups = computed(
  () =>
    filterObject(
      args.value.reduce(
        (groups, arg) => {
          const filterKey = arg.filter.filterKeys.find((key) => Filters[key] !== undefined)
          const group: FilterGroups = filterKey ? Filters[filterKey].group : FilterGroups.Default

          const g = groups[group] ?? []
          return { ...groups, [group]: g.concat([arg]) }
        },
        // This ensures the order of the groups is always the same
        Object.values(FilterGroups).reduce((acc, val) => ({ ...acc, [val]: [] }), {}),
      ),
      // Remove the empty groups from the list
      (_, val: FilterMetaDataProp[]) => val.length > 0,
    ) as Record<FilterGroups, FilterMetaDataProp[]>,
)

const resetAllFilters = () => {
  resetFilters(activeFilterValues)
}

const buildEncoding = (name, value) =>
  useQueryEncoder(
    name,
    value,
    (raw, _) => (raw !== '' ? JSON.parse(atob(raw)) : value),
    (data, _) => (data !== undefined && data !== null ? btoa(JSON.stringify(data)) : ''),
  )

watch(args, () => {
  for (const { filter } of args.value) {
    activeFilterValues.value[filter.inputArgName] = buildEncoding(filter.inputArgName, undefined)
  }
})
</script>

<style lang="scss" scoped>
.field-table {
  margin-top: 3rem;
  &:deep(.table-wrap) {
    max-height: 300px;
    overflow: auto;
  }
}

.query-table {
  margin-top: 3rem;
  &:deep(.table-wrap) {
    overflow: auto;
  }
}
</style>
